import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface RelevanceReviewItem {
  id: string;
  rawBundleId: string;
  reviewedAt: string | null;
  reviewedRelevant: boolean | null;
  reviewedReason: string | null;
  aiRelevant: boolean | null;
  aiVerdictReason: string | null;
}

export type BundleRelevanceStatus =
  | { kind: "not-assessed" }
  | { kind: "assessment-error"; reason: string | null }
  | { kind: "decided-no-review-item"; relevant: boolean; reason: string }
  | { kind: "pending-review"; reviewItem: RelevanceReviewItem }
  | { kind: "reviewed"; reviewItem: RelevanceReviewItem };

async function fetchBundleRelevanceStatusesForEncounter(
  encounterId: string,
): Promise<Map<string, BundleRelevanceStatus>> {
  const [{ data: reviewItems, error: reviewItemsError }, { data: assessments, error: assessmentsError }] =
    await Promise.all([
      supabase
        .from("encounter_relevance_review_items")
        .select("id, raw_bundle_id, reviewed_at, reviewed_relevant, reviewed_reason")
        .eq("encounter_id", encounterId),
      supabase
        .from("encounter_relevance_assessments")
        .select("raw_bundle_id, status, verdict_reason")
        .eq("encounter_id", encounterId),
    ]);
  if (reviewItemsError) throw reviewItemsError;
  if (assessmentsError) throw assessmentsError;

  const assessmentByBundleId = new Map((assessments ?? []).map((a) => [a.raw_bundle_id, a] as const));
  const statusByBundleId = new Map<string, BundleRelevanceStatus>();

  for (const item of reviewItems ?? []) {
    const assessment = assessmentByBundleId.get(item.raw_bundle_id);
    const aiRelevant = assessment?.status === "relevant" ? true : assessment?.status === "not_relevant" ? false : null;
    const reviewItem: RelevanceReviewItem = {
      id: item.id,
      rawBundleId: item.raw_bundle_id,
      reviewedAt: item.reviewed_at,
      reviewedRelevant: item.reviewed_relevant,
      reviewedReason: item.reviewed_reason,
      aiRelevant,
      aiVerdictReason: assessment?.verdict_reason ?? null,
    };
    statusByBundleId.set(
      item.raw_bundle_id,
      item.reviewed_at ? { kind: "reviewed", reviewItem } : { kind: "pending-review", reviewItem },
    );
  }

  for (const assessment of assessmentByBundleId.values()) {
    if (statusByBundleId.has(assessment.raw_bundle_id)) continue;

    if (assessment.status === "pending" || assessment.status === "processing") {
      statusByBundleId.set(assessment.raw_bundle_id, { kind: "not-assessed" });
    } else if (assessment.status === "error") {
      statusByBundleId.set(assessment.raw_bundle_id, {
        kind: "assessment-error",
        reason: assessment.verdict_reason,
      });
    } else {
      statusByBundleId.set(assessment.raw_bundle_id, {
        kind: "decided-no-review-item",
        relevant: assessment.status === "relevant",
        reason: assessment.verdict_reason ?? "",
      });
    }
  }

  return statusByBundleId;
}

export function useBundleRelevanceStatusesForEncounter(encounterId: string | null) {
  return useQuery({
    queryKey: ["bundle-relevance-statuses", encounterId],
    queryFn: () => fetchBundleRelevanceStatusesForEncounter(encounterId as string),
    enabled: !!encounterId,
  });
}

export interface BundleRelevanceStatusesQuery {
  data: Map<string, BundleRelevanceStatus> | undefined;
  isLoading: boolean;
  error: Error | null;
}

export function resolveBundleRelevanceStatus(
  bundleId: string,
  statuses: BundleRelevanceStatusesQuery,
): BundleRelevanceStatus | null {
  if (statuses.isLoading) return null;
  if (statuses.error && !statuses.data) return null;
  return statuses.data?.get(bundleId) ?? { kind: "not-assessed" };
}
